/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'../../common/DataPointActionBase',
	'../api/DataPointActionsProviderAPI',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../../widgets/livewidget/nls/StringResources',
	'underscore'
], (DataPointActionBase, DataPointActionsProviderAPI, APIFactory, stringResources, _) => {
	/**
	 * The base class of data point filter actions.
	 *
	 * User selections are built by the user as 'pending filters' usually as a form of multiselect
	 * (using the ctrl/command key or long press).
	 *
	 * Pressing the filter button transfers these filters from pending to actual.
	 */
	class DataPointFilterAction extends DataPointActionBase {
		constructor({ content, dashboardAPI, features }) {
			super({ content, dashboardAPI, features });
			this.content.getFeature('DataPointActions').registerProvider('DataPointFilterAction', this.getAPI());
			this.icons = features['Dashboard.Icons'];
		}

		getAPI() {
			if (!this._api) {
				this._api = APIFactory.createAPI(this, [DataPointActionsProviderAPI]);
			}
			return this._api;
		}

		getDataPointActionList(selections, actionOptions = {}) {
			let actionList = [];
			const filterExclude = this.icons.getIcon('wfg_filter_exclude');
			const filterKeep = this.icons.getIcon('wfg_filter_keep');
			const filterIcon = this.icons.getIcon('common-filter');

			if (this._supportAction(selections, actionOptions)) {
				actionList.push(...[{
					name: 'filterLocalExcludeSelected',
					label: stringResources.get('toolbarActionFilterLocalExcludeSelected'),
					className: 'iconWithText',
					icon: filterExclude.id,
					text: stringResources.get('toolbarActionFilterLocalExcludeSelected'),
					type:'Button',
					actions: {
						apply: this._applyFilter.bind(this, selections, /** exclude */true)
					},
					closeOnActionApplied: true
				}, {
					name: 'filterLocalKeepSelected',
					label: stringResources.get('toolbarActionFilterLocalKeepSelected'),
					className: 'iconWithText',
					icon: filterKeep.id,
					text: stringResources.get('toolbarActionFilterLocalKeepSelected'),
					type:'Button',
					actions: {
						apply: this._applyFilter.bind(this, selections, /** exclude */ false)
					},
					closeOnActionApplied: true
				}]
				);
				if (actionOptions.includeApplyFilter && selections.area !== 'cell_edge_row' && selections.area !== 'cell_edge_column') {
					actionList.push({
						name: 'filterAllKeepSelected',
						label: stringResources.get('toolbarActionFilterAllKeepSelected'),
						className: 'iconWithText',
						icon: filterIcon.id,
						text: stringResources.get('toolbarActionFilterAllKeepSelected'),
						type:'Button',
						actions: {
							apply: this._applyPendingToPageContext.bind(this, selections)
						},
						closeOnActionApplied: true
					});
				}
			}
			return actionList;
		}

		_supportAction(selections, actionOptions) {
			if (actionOptions.noFilters) {
				return false;
			}
			const dataPoints = selections && selections.dataPoints;
			return _.some(dataPoints, dataPoint => dataPoint.categories);
		}

		_applyPendingToPageContext(selections, transactionToken) {
			const transaction = this.dashboard.getFeature('Transaction');
			const localTransactionToken = transaction.startTransaction(transactionToken);
			const itemContext = this._getSelector();
			const groupedSelections = this._groupSelections(selections.dataPoints);
			const globalFilters = this.globalFilters.getFilters(itemContext.sourceId, itemContext.scope);
			for (let groupKey in groupedSelections) {
				if (!groupKey) {
					continue;
				}

				itemContext.itemIds = groupKey.split(',');
				let filterContext = {
					values: groupedSelections[groupKey]
				};
				globalFilters.addFilter(itemContext, filterContext, localTransactionToken);
			}
			transaction.endTransaction(localTransactionToken);
		}

		_applyFilter(selections, exclude, transactionToken) {
			const transaction = this.dashboard.getFeature('Transaction');
			const localTransactionToken = transaction.startTransaction(transactionToken);
			const groupedSelections = this._groupSelections(selections.dataPoints);
			const slots = this._getVisualization().getSlots();
			for (let groupKey in groupedSelections) {
				if (!groupKey) {
					continue;
				}
				let autoBinList = [];
				let itemContext = {};
				let filterContext = {
					exclude: !!exclude
				};

				const currentGroupValues = groupedSelections[groupKey];
				this._addAutoBins(currentGroupValues[0], autoBinList, slots);
				if (autoBinList.length) {
					itemContext.binning = autoBinList;
				}
				const itemIds = groupKey.split(',');
				if (itemIds.length > 1) {
					// Data point filter
					itemContext.itemIds = itemIds;
					filterContext.values = currentGroupValues;
				} else {
					// Edge filter
					itemContext.itemId = itemIds[0];
					filterContext.values = _.flatten(currentGroupValues);
				}
				this._getLocalFilters().addFilter(itemContext, filterContext, localTransactionToken);
				if (filterContext.exclude) {
					this.globalFilters.removeFilters(this._getSelector(), localTransactionToken);
				}
			}
			transaction.endTransaction(localTransactionToken);
		}

		_addAutoBins(valueList, autoBinList, slots) {
			// TODO Should make this more generic while working on filter epic.
			_.each(valueList, value => {
				const mappingInfo = slots.getMappingInfo(value.dataItemId);
				const dataItem = mappingInfo && mappingInfo.dataItem;
				let autoBin = dataItem && dataItem.getBinning();
				// Add if not exists
				if (autoBin && _.pluck(autoBinList, 'dataItemId').indexOf(dataItem.getId() === -1)) {
					autoBinList.push(value);
				}
			});
		}
	}

	return DataPointFilterAction;

});
